import { useState } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';
import Button from '../../../components/base/Button';
import Card from '../../../components/base/Card';

interface Template {
  id: string;
  name: string;
  description: string;
  backgroundImage: string;
  orientation: 'portrait' | 'landscape';
}

interface Student {
  name: string;
  email: string;
  courseName: string;
  completionDate: string;
  status: 'success' | 'error';
  errorMessage?: string;
}

interface CertificateSettings {
  isPaid: boolean;
  price: number;
  maxDownloads: number;
}

export default function BulkIssuePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [certificateSettings, setCertificateSettings] = useState<CertificateSettings>({
    isPaid: false,
    price: 2000,
    maxDownloads: 3
  });

  const templates: Template[] = [
    {
      id: '1',
      name: '수료증 템플릿',
      description: '기본 수료증 템플릿',
      backgroundImage: 'https://public.readdy.ai/ai/img_res/b5166db431184b5ab5d924178d493f55.jpg',
      orientation: 'landscape'
    },
    {
      id: '2',
      name: '세로형 수료증',
      description: '세로 방향 수료증 템플릿',
      backgroundImage: 'https://readdy.ai/api/search-image?query=vertical%20certificate%20template%20with%20elegant%20border%2C%20portrait%20orientation%20with%20formal%20design%2C%20clean%20white%20background%20with%20decorative%20frame&width=600&height=800&seq=cert2&orientation=portrait',
      orientation: 'portrait'
    },
    {
      id: '3',
      name: '모던 수료증',
      description: '현대적인 디자인의 수료증',
      backgroundImage: 'https://readdy.ai/api/search-image?query=modern%20certificate%20template%20with%20minimalist%20design%2C%20clean%20lines%20and%20contemporary%20style%2C%20professional%20layout%20with%20simple%20border&width=800&height=600&seq=cert3&orientation=landscape',
      orientation: 'landscape'
    }
  ];

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    
    const file = event.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(file.type)) {
      alert('Excel 파일(.xlsx, .xls) 또는 CSV 파일만 업로드 가능합니다.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('파일 크기는 10MB를 초과할 수 없습니다.');
      return;
    }

    setUploadedFile(file);
    
    // 샘플 데이터 생성 (실제로는 파일을 파싱해야 함)
    const sampleStudents: Student[] = [
      {
        name: '홍길동',
        email: 'hong@example.com',
        courseName: 'React 개발 과정',
        completionDate: '2024-01-20',
        status: 'success'
      },
      {
        name: '김영희',
        email: 'kim@example.com',
        courseName: 'React 개발 과정',
        completionDate: '2024-01-20',
        status: 'success'
      },
      {
        name: '이철수',
        email: 'lee@example.com',
        courseName: 'React 개발 과정',
        completionDate: '2024-01-20',
        status: 'success'
      },
      {
        name: '박민수',
        email: 'park@example.com',
        courseName: 'React 개발 과정',
        completionDate: '2024-01-20',
        status: 'success'
      },
      {
        name: '정수진',
        email: 'jung@example.com',
        courseName: 'React 개발 과정',
        completionDate: '2024-01-20',
        status: 'success'
      },
      {
        name: '최동욱',
        email: 'invalid-email',
        courseName: 'React 개발 과정',
        completionDate: '2024-01-20',
        status: 'error',
        errorMessage: '이메일 형식 오류'
      }
    ];
    
    setStudents(sampleStudents);
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !selectedTemplate) {
      alert('템플릿을 선택해주세요.');
      return;
    }
    if (currentStep === 2 && !uploadedFile) {
      alert('파일을 업로드해주세요.');
      return;
    }
    if (currentStep === 3 && certificateSettings.isPaid && certificateSettings.price < 2000) {
      alert('유료 수료증 금액은 최소 2,000원 이상이어야 합니다.');
      return;
    }
    if (currentStep === 3 && certificateSettings.isPaid && certificateSettings.maxDownloads < 1) {
      alert('다운로드 횟수는 최소 1회 이상이어야 합니다.');
      return;
    }
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBulkIssue = () => {
    setShowConfirmModal(true);
  };

  const confirmBulkIssue = () => {
    setShowConfirmModal(false);
    // 실제 발급 로직 구현
    alert('일괄 발급이 완료되었습니다!');
    // 초기화
    setCurrentStep(1);
    setSelectedTemplate(null);
    setUploadedFile(null);
    setStudents([]);
    setCertificateSettings({
      isPaid: false,
      price: 2000,
      maxDownloads: 3
    });
  };

  const successCount = students.filter(s => s.status === 'success').length;
  const errorCount = students.filter(s => s.status === 'error').length;

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">일괄 발급</h1>
          <p className="text-gray-600 mt-1">엑셀 파일을 업로드하여 증명서를 일괄 발급합니다</p>
        </div>

        {/* 진행 단계 표시 */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step}
                  </div>
                  <span className={`mt-2 text-sm font-medium ${
                    currentStep >= step ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {step === 1 ? '템플릿 선택' : step === 2 ? '파일 업로드' : step === 3 ? '유료/무료 설정' : '발급 확인'}
                  </span>
                </div>
                {step < 4 && (
                  <div className={`h-1 flex-1 mx-4 ${
                    currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 1단계: 템플릿 선택 */}
        {currentStep === 1 && (
          <div>
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">템플릿 선택</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className={`border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                      selectedTemplate?.id === template.id
                        ? 'border-blue-600 shadow-lg'
                        : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    <div className="aspect-video bg-gray-50 overflow-hidden relative">
                      <img 
                        src={template.backgroundImage} 
                        alt={template.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 bg-black/70 text-white text-xs rounded-md flex items-center w-fit h-fit justify-center">
                          <i className={`${template.orientation === 'landscape' ? 'ri-landscape-line' : 'ri-smartphone-line'} mr-1`}></i>
                          {template.orientation === 'landscape' ? 'A4 가로' : 'A4 세로'}
                        </span>
                      </div>
                      {selectedTemplate?.id === template.id && (
                        <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center">
                          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-2xl text-white"></i>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
                      <p className="text-sm text-gray-600">{template.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <div className="flex justify-end">
              <Button 
                onClick={handleNextStep}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 whitespace-nowrap"
                disabled={!selectedTemplate}
              >
                다음 단계
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </div>
          </div>
        )}

        {/* 2단계: 파일 업로드 */}
        {currentStep === 2 && (
          <div>
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">엑셀 파일 업로드</h3>
              
              {!uploadedFile ? (
                <div>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label 
                    htmlFor="file-upload" 
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`w-full border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors block ${
                      isDragging 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-4 block"></i>
                    <p className="text-lg font-medium text-gray-700 mb-2">파일을 선택하거나 드래그하세요</p>
                    <p className="text-gray-500">Excel 파일(.xlsx, .xls) 또는 CSV 파일 (최대 10MB)</p>
                  </label>
                </div>
              ) : (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <i className="ri-file-excel-line text-2xl text-green-600 mr-3"></i>
                        <div>
                          <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                          <p className="text-sm text-gray-600">{(uploadedFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          setUploadedFile(null);
                          setStudents([]);
                        }}
                        className="text-red-600 hover:bg-red-50 border border-red-300 px-3 py-2 whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </Button>
                    </div>
                  </div>

                  {/* 발급 명단 미리보기 */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">발급 대상자 목록</h4>
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-600 font-medium">
                          <i className="ri-checkbox-circle-line mr-1"></i>
                          정상: {successCount}건
                        </span>
                        <span className="text-red-600 font-medium">
                          <i className="ri-error-warning-line mr-1"></i>
                          오류: {errorCount}건
                        </span>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">번호</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">학습자명</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">이메일</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">과정명</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">수료일</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">템플릿</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {students.map((student, index) => (
                              <tr key={index} className={student.status === 'error' ? 'bg-red-50' : ''}>
                                <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{student.name}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{student.email}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{student.courseName}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{student.completionDate}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{selectedTemplate?.name}</td>
                                <td className="px-4 py-3 text-sm">
                                  {student.status === 'success' ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                      <i className="ri-checkbox-circle-line mr-1"></i>
                                      정상
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                      <i className="ri-error-warning-line mr-1"></i>
                                      {student.errorMessage}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {errorCount > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <i className="ri-error-warning-line text-yellow-600 text-xl mr-3 mt-0.5"></i>
                        <div>
                          <h5 className="font-medium text-yellow-900 mb-1">오류 항목 확인 필요</h5>
                          <p className="text-sm text-yellow-800">
                            {errorCount}건의 오류가 발견되었습니다. 오류 항목은 발급되지 않습니다.
                            엑셀 파일을 수정한 후 다시 업로드해주세요.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>

            <div className="flex justify-between">
              <Button 
                onClick={handlePrevStep}
                className="border border-gray-300 hover:bg-gray-50 px-6 py-3 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                이전 단계
              </Button>
              <Button 
                onClick={handleNextStep}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 whitespace-nowrap"
                disabled={!uploadedFile || students.length === 0}
              >
                다음 단계
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </div>
          </div>
        )}

        {/* 3단계: 유료/무료 설정 */}
        {currentStep === 3 && (
          <div>
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold mb-6">수료증 유료/무료 설정</h3>
              
              <div className="space-y-6">
                {/* 유료/무료 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">발급 유형</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                      onClick={() => setCertificateSettings({ ...certificateSettings, isPaid: false })}
                      className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                        !certificateSettings.isPaid
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 ${
                            !certificateSettings.isPaid
                              ? 'border-blue-600 bg-blue-600'
                              : 'border-gray-300'
                          }`}>
                            {!certificateSettings.isPaid && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </div>
                          <h4 className="font-semibold text-gray-900">무료 발급</h4>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full whitespace-nowrap">
                          무제한 다운로드
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 ml-9">
                        학습자가 무료로 수료증을 다운로드할 수 있습니다. 다운로드 횟수 제한이 없습니다.
                      </p>
                    </div>

                    <div
                      onClick={() => setCertificateSettings({ ...certificateSettings, isPaid: true })}
                      className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                        certificateSettings.isPaid
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 ${
                            certificateSettings.isPaid
                              ? 'border-blue-600 bg-blue-600'
                              : 'border-gray-300'
                          }`}>
                            {certificateSettings.isPaid && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </div>
                          <h4 className="font-semibold text-gray-900">유료 발급</h4>
                        </div>
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full whitespace-nowrap">
                          횟수 제한
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 ml-9">
                        학습자가 결제 후 수료증을 다운로드할 수 있습니다. 다운로드 횟수를 설정할 수 있습니다.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 유료 설정 옵션 */}
                {certificateSettings.isPaid && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-settings-3-line mr-2 text-purple-600"></i>
                      유료 수료증 상세 설정
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* 금액 설정 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          수료증 금액 <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={certificateSettings.price}
                            onChange={(e) => setCertificateSettings({ 
                              ...certificateSettings, 
                              price: parseInt(e.target.value) || 0 
                            })}
                            min="2000"
                            step="1000"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 pr-12"
                            placeholder="2000"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                            원
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          최소 금액: 2,000원 (플랫폼 수수료 15% 별도)
                        </p>
                        <div className="mt-3 p-3 bg-white rounded-lg border border-purple-200">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">학습자 결제 금액:</span>
                            <span className="font-semibold text-gray-900">
                              {certificateSettings.price.toLocaleString()}원
                            </span>
                          </div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">플랫폼 수수료 (15%):</span>
                            <span className="font-semibold text-red-600">
                              -{Math.floor(certificateSettings.price * 0.15).toLocaleString()}원
                            </span>
                          </div>
                          <div className="border-t border-purple-200 pt-2 mt-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-700 font-medium">기관 정산 금액:</span>
                              <span className="font-bold text-purple-600">
                                {Math.floor(certificateSettings.price * 0.85).toLocaleString()}원
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 다운로드 횟수 설정 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          다운로드 가능 횟수 <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={certificateSettings.maxDownloads}
                            onChange={(e) => setCertificateSettings({ 
                              ...certificateSettings, 
                              maxDownloads: parseInt(e.target.value) || 1 
                            })}
                            min="1"
                            max="10"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 pr-12"
                            placeholder="3"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                            회
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          학습자가 다운로드할 수 있는 최대 횟수 (1~10회)
                        </p>
                        <div className="mt-3 p-3 bg-white rounded-lg border border-purple-200">
                          <div className="flex items-start">
                            <i className="ri-information-line text-purple-600 mr-2 mt-0.5"></i>
                            <div className="text-xs text-gray-600">
                              <p className="mb-1">• 설정한 횟수만큼 다운로드 가능</p>
                              <p className="mb-1">• 횟수 초과 시 추가 결제 필요</p>
                              <p>• 권장 횟수: 3~5회</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 예상 수익 계산 */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg border border-purple-300">
                      <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <i className="ri-money-dollar-circle-line mr-2 text-purple-600"></i>
                        예상 수익 계산
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">발급 예정 건수</p>
                          <p className="text-xl font-bold text-gray-900">{successCount}건</p>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">총 판매 예상액</p>
                          <p className="text-xl font-bold text-blue-600">
                            {(certificateSettings.price * successCount).toLocaleString()}원
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">예상 정산 금액</p>
                          <p className="text-xl font-bold text-purple-600">
                            {Math.floor(certificateSettings.price * successCount * 0.85).toLocaleString()}원
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 안내 메시지 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
                    <div>
                      <h5 className="font-medium text-blue-900 mb-2">안내사항</h5>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• 무료 발급: 학습자가 언제든지 무제한으로 다운로드 가능</li>
                        <li>• 유료 발급: 학습자가 결제 후 설정한 횟수만큼 다운로드 가능</li>
                        <li>• 유료 수료증의 플랫폼 수수료는 15%입니다</li>
                        <li>• 발급 후에는 유료/무료 설정을 변경할 수 없습니다</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex justify-between">
              <Button 
                onClick={handlePrevStep}
                className="border border-gray-300 hover:bg-gray-50 px-6 py-3 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                이전 단계
              </Button>
              <Button 
                onClick={handleNextStep}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 whitespace-nowrap"
              >
                다음 단계
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </div>
          </div>
        )}

        {/* 4단계: 발급 확인 */}
        {currentStep === 4 && (
          <div>
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold mb-6">발급 정보 확인</h3>
              
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">선택된 템플릿</p>
                    <p className="text-lg font-semibold text-gray-900">{selectedTemplate?.name}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">업로드된 파일</p>
                    <p className="text-lg font-semibold text-gray-900">{uploadedFile?.name}</p>
                  </div>
                </div>

                {/* 유료/무료 정보 */}
                <div className={`rounded-lg p-4 border-2 ${
                  certificateSettings.isPaid 
                    ? 'bg-purple-50 border-purple-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <i className={`${certificateSettings.isPaid ? 'ri-money-dollar-circle-line text-purple-600' : 'ri-gift-line text-green-600'} mr-2 text-xl`}></i>
                      {certificateSettings.isPaid ? '유료 수료증' : '무료 수료증'}
                    </h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      certificateSettings.isPaid
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {certificateSettings.isPaid ? '결제 필요' : '무료 제공'}
                    </span>
                  </div>
                  
                  {certificateSettings.isPaid ? (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">수료증 금액</p>
                        <p className="text-lg font-bold text-gray-900">
                          {certificateSettings.price.toLocaleString()}원
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">다운로드 횟수</p>
                        <p className="text-lg font-bold text-gray-900">
                          {certificateSettings.maxDownloads}회
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">건당 정산액</p>
                        <p className="text-lg font-bold text-purple-600">
                          {Math.floor(certificateSettings.price * 0.85).toLocaleString()}원
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-sm text-gray-600">
                        학습자가 무료로 무제한 다운로드할 수 있습니다.
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-blue-600 mb-1">총 발급 예정</p>
                    <p className="text-3xl font-bold text-blue-700">{students.length}</p>
                    <p className="text-xs text-blue-600 mt-1">건</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-green-600 mb-1">정상 발급</p>
                    <p className="text-3xl font-bold text-green-700">{successCount}</p>
                    <p className="text-xs text-green-600 mt-1">건</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-red-600 mb-1">오류 건수</p>
                    <p className="text-3xl font-bold text-red-700">{errorCount}</p>
                    <p className="text-xs text-red-600 mt-1">건</p>
                  </div>
                </div>

                {certificateSettings.isPaid && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <i className="ri-calculator-line mr-2 text-purple-600"></i>
                      예상 수익 요약
                    </h5>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">총 판매 예상액</p>
                        <p className="text-xl font-bold text-blue-600">
                          {(certificateSettings.price * successCount).toLocaleString()}원
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">플랫폼 수수료</p>
                        <p className="text-xl font-bold text-red-600">
                          -{Math.floor(certificateSettings.price * successCount * 0.15).toLocaleString()}원
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">예상 정산 금액</p>
                        <p className="text-xl font-bold text-purple-600">
                          {Math.floor(certificateSettings.price * successCount * 0.85).toLocaleString()}원
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
                    <div>
                      <h5 className="font-medium text-blue-900 mb-2">발급 안내</h5>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• 증명서는 각 학습자의 이메일로 자동 발송됩니다</li>
                        <li>• 발급 완료 후 증명서 관리 페이지에서 확인할 수 있습니다</li>
                        <li>• 오류가 있는 항목은 발급되지 않습니다</li>
                        {certificateSettings.isPaid && (
                          <>
                            <li>• 유료 수료증은 학습자가 결제 후 다운로드할 수 있습니다</li>
                            <li>• 정산 금액은 매월 말일에 정산됩니다</li>
                          </>
                        )}
                        <li>• 발급 후에는 취소할 수 없으니 신중히 확인해주세요</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex justify-between">
              <Button 
                onClick={handlePrevStep}
                className="border border-gray-300 hover:bg-gray-50 px-6 py-3 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                이전 단계
              </Button>
              <Button 
                onClick={handleBulkIssue}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 whitespace-nowrap"
              >
                <i className="ri-send-plane-line mr-2"></i>
                최종 생성
              </Button>
            </div>
          </div>
        )}

        {/* 확인 모달 */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-error-warning-line text-2xl text-yellow-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">일괄 발급 확인</h3>
                <p className="text-gray-600 text-center mb-6">
                  총 <strong className="text-blue-600">{successCount}건</strong>의 {certificateSettings.isPaid ? '유료 ' : '무료 '}증명서를 발급하시겠습니까?
                </p>

                {certificateSettings.isPaid && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <i className="ri-money-dollar-circle-line mr-2 text-purple-600"></i>
                      유료 수료증 정보
                    </h4>
                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex justify-between">
                        <span>수료증 금액:</span>
                        <span className="font-medium">{certificateSettings.price.toLocaleString()}원</span>
                      </div>
                      <div className="flex justify-between">
                        <span>다운로드 횟수:</span>
                        <span className="font-medium">{certificateSettings.maxDownloads}회</span>
                      </div>
                      <div className="flex justify-between">
                        <span>예상 정산 금액:</span>
                        <span className="font-medium text-purple-600">
                          {Math.floor(certificateSettings.price * successCount * 0.85).toLocaleString()}원
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">플랜별 발급 제한</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Free 플랜:</span>
                      <span className="font-medium">월 100건</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Basic 플랜:</span>
                      <span className="font-medium">월 1,000건</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pro 플랜:</span>
                      <span className="font-medium">월 10,000건</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Enterprise 플랜:</span>
                      <span className="font-medium">무제한</span>
                    </div>
                  </div>
                </div>

                {errorCount > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                    <p className="text-sm text-red-800 text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      오류 {errorCount}건은 발급되지 않습니다
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowConfirmModal(false)}
                    className="flex-1 border border-gray-300 hover:bg-gray-50 py-3 whitespace-nowrap"
                  >
                    아니오
                  </Button>
                  <Button
                    onClick={confirmBulkIssue}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 whitespace-nowrap"
                  >
                    네, 발급합니다
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
